// src/pages/Company.jsx
import { motion } from 'framer-motion';
import { MainLayout } from 'components/Layout';
import { useEffect } from 'react';

export const Company = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <MainLayout
      title="About — Northup Technologies"
      description="Learn about our mission, our culture, and the technology shaping Africa’s digital transformation."
    >
      <div className="mx-auto max-w-6xl pb-24">
        {/* HERO */}
        <motion.section
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
          className="mb-20 space-y-6 text-center"
        >
          <span className="inline-block rounded-full border border-slate-700/70 bg-slate-900/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.21em] text-[#9D5666] backdrop-blur">
            About Northup
          </span>

          <h1 className="text-4xl font-semibold text-slate-50 sm:text-5xl lg:text-6xl">
            Building the Digital Infrastructure for Africa’s Next Decade
          </h1>

          <p className="mx-auto max-w-2xl text-sm text-slate-400 sm:text-base">
            We build technologies that empower businesses, creators, and everyday people across the
            continent—through beautifully engineered products, scalable cloud systems, and
            culture-shaping user experiences.
          </p>
        </motion.section>

        {/* MISSION & VISION */}
        <section className="mb-24 grid gap-10 md:grid-cols-2">
          <InfoCard
            title="Our Mission"
            description="To create technology ecosystems that simplify life, empower communities, and accelerate Africa’s digital evolution."
          />

          <InfoCard
            title="Our Vision"
            description="A continent where individuals and businesses seamlessly connect, transact, and build—powered by world-class African technology."
          />
        </section>

        {/* CULTURE */}
        <section className="mb-24">
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-6 text-center text-3xl font-semibold text-slate-50"
          >
            The Culture We’re Building
          </motion.h2>

          <div className="grid gap-6 md:grid-cols-3">
            <ValueCard
              title="Human-Centered"
              description="People first. We design products that solve real problems and enhance everyday life."
            />
            <ValueCard
              title="Reliable & Scalable"
              description="We engineer systems that remain fast, resilient, and trustworthy—even at continent-scale."
            />
            <ValueCard
              title="Bold Innovation"
              description="We pursue ideas that challenge assumptions and push African technology forward."
            />
          </div>
        </section>

        {/* TIMELINE */}
        <section className="mb-24">
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-10 text-center text-3xl font-semibold text-slate-50"
          >
            Our Journey So Far
          </motion.h2>

          <div className="space-y-10 border-l border-slate-800/70 pl-6">
            <TimelineItem
              year="2024"
              title="Northup Technologies is founded"
              description="Created to solve the fragmented digital experience across African platforms."
            />
            <TimelineItem
              year="2024"
              title="JustKnow begins development"
              description="We set out to build the first social-commerce-mobility super-app built entirely in Africa."
            />
            <TimelineItem
              year="2025"
              title="Private beta launch"
              description="JustKnow begins testing with merchants, riders, and early adopters across Uganda."
            />
          </div>
        </section>

        {/* TEAM SECTION */}
        <section className="mb-24">
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-10 text-center text-3xl font-semibold text-slate-50"
          >
            Meet the Team
          </motion.h2>

          {/* Directors */}
          <div className="mb-16">
            <h3 className="mb-6 text-center text-xl font-semibold text-slate-200">Directors</h3>

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              <TeamMember name="Emmanuel Ogwal" role="CEO, Managing Director" />
              <TeamMember name="Director Name" role="Operations Director" />
              <TeamMember name="Director Name" role="Finance & Strategy Director" />
            </div>
          </div>

          {/* Developers */}
          <div>
            <h3 className="mb-6 text-center text-xl font-semibold text-slate-200">
              Development Team
            </h3>

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-2">
              <TeamMember name="Jonathan Mwebaze" role="Team Lead, Backend Engineer" />
              <TeamMember name="Emmanuel Lubwama" role="Mobile Engineer" />
            </div>
          </div>
        </section>

        {/* ADDRESS / CONTACT SECTION */}
        <section className="mb-24">
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-8 text-center text-3xl font-semibold text-slate-50"
          >
            Visit Us or Get in Touch
          </motion.h2>

          <div className="grid gap-8 md:grid-cols-[1.1fr_0.9fr]">
            {/* LEFT: INFO */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-slate-800/70 bg-slate-900/50 p-6 shadow-lg backdrop-blur"
            >
              <h3 className="text-xl font-semibold text-slate-50">Our Office</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Uganda Central Kampala,
                <br />
                Central Division Nakasero II Lourdel,
                <br />
                P.O.Box 150570 Kampala GPO 7 Lourdel
              </p>

              <div className="mt-6 space-y-3 text-sm text-slate-300">
                <div>
                  <span className="font-medium text-slate-50">Phone:</span> +256 (0) 773 465 177
                </div>
                <div>
                  <span className="font-medium text-slate-50">Email:</span> support@northup.ug
                </div>
                <div>
                  <span className="font-medium text-slate-50">Website:</span> www.northup.ug
                </div>
              </div>
            </motion.div>

            {/* RIGHT: MAP MOCK */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="relative h-80 overflow-hidden rounded-2xl border border-slate-800/70 bg-slate-900/50 shadow-lg"
            >
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-fuchsia-500/10 opacity-50"
              />

              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative flex h-24 w-24 items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-cyan-500/20 blur-xl" />
                  <div className="absolute h-16 w-16 rounded-full border-2 border-[#9D5666]/60 bg-slate-900/70 backdrop-blur" />
                  <div className="absolute h-3 w-3 rounded-full bg-[#9D5666]" />
                </div>
              </div>

              <p className="absolute bottom-4 left-4 text-xs text-slate-400">
                Kampala, Uganda — Headquarters
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <FinalCTA />
      </div>
    </MainLayout>
  );
};

/* ------------------------------------------------------ *
 * Reusable Components
 * ------------------------------------------------------ */

const InfoCard = ({ title, description }) => (
  <motion.div
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="rounded-2xl border border-slate-800/70 bg-slate-900/60 p-7 backdrop-blur shadow-lg"
  >
    <h3 className="text-xl font-semibold text-slate-100">{title}</h3>
    <p className="mt-3 text-sm leading-relaxed text-slate-400">{description}</p>
  </motion.div>
);

const ValueCard = ({ title, description }) => (
  <motion.div
    whileHover={{
      y: -4,
      scale: 1.02,
      boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
    }}
    transition={{ type: 'spring', stiffness: 260, damping: 20 }}
    className="rounded-2xl border border-slate-800/70 bg-slate-900/60 p-6 backdrop-blur"
  >
    <h4 className="text-lg font-semibold text-slate-100">{title}</h4>
    <p className="mt-2 text-sm text-slate-400">{description}</p>
  </motion.div>
);

const TimelineItem = ({ year, title, description }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="relative pl-6"
  >
    <div className="absolute left-[-14px] top-1 h-3 w-3 rounded-full bg-[#9D5666]/80" />
    <p className="text-xs font-semibold text-[#9D5666]">{year}</p>
    <h4 className="text-base font-semibold text-slate-100">{title}</h4>
    <p className="mt-1 text-sm text-slate-400">{description}</p>
  </motion.div>
);

/* TEAM MEMBER COMPONENT */
const TeamMember = ({ name, role }) => (
  <motion.div
    whileHover={{
      y: -4,
      scale: 1.02,
      boxShadow: '0 20px 40px rgba(0,0,0,0.45)',
    }}
    transition={{ type: 'spring', stiffness: 260, damping: 20 }}
    className="rounded-2xl border border-slate-800/70 bg-slate-900/60 p-6 text-center backdrop-blur"
  >
    <div className="mx-auto mb-4 h-20 w-20 rounded-full bg-gradient-to-br from-cyan-500/20 via-purple-500/20 to-fuchsia-500/20"></div>
    <h4 className="text-lg font-semibold text-slate-100">{name}</h4>
    <p className="text-sm text-slate-400">{role}</p>
  </motion.div>
);

const FinalCTA = () => (
  <motion.div
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="mx-auto mt-12 max-w-3xl rounded-3xl border border-slate-800 bg-slate-900/60 p-10 text-center shadow-[0_0_40px_rgba(56,189,248,0.12)] backdrop-blur-xl"
  >
    <h3 className="text-2xl font-semibold text-slate-50">
      We’re building Africa’s next technology wave.
    </h3>
    <p className="mt-3 text-sm text-slate-400">
      Want to partner, invest, or collaborate with us? Reach out anytime.
    </p>

    <button className="mt-6 rounded-full bg-gradient-to-r from-[#9D5666] via-[#C4C5C7] to-[#9D5666] px-6 py-3 text-sm font-semibold text-slate-950 shadow-lg transition hover:brightness-110">
      Contact the Team
    </button>
  </motion.div>
);
